import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Button,
  Card,
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@predoc/cow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordRequirementsSection } from './PasswordRequirementsSection';
import { PasswordInput } from '@/components';
import { toast } from 'sonner';
import { Footer } from './Footer';
import { AUTH_ASSETS, AUTH_PATHS } from '../constants';
import { newPasswordSchema, type NewPasswordFormData } from '../types';
import { useConfirmSignIn } from '../hooks/authApi';

export const NewPasswordRequired = () => {
  const navigate = useNavigate();
  const { mutateAsync, isPending, error } = useConfirmSignIn();

  const form = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = form.watch('password');

  // Navigate to login if user is not in new password required flow
  useEffect(() => {
    const validateAuthState = async () => {
      const hasNewPasswordRequiredFlag =
        sessionStorage.getItem('newPasswordRequired') === 'true';

      if (!hasNewPasswordRequiredFlag) {
        navigate(AUTH_PATHS.LOGIN);
        return;
      }

      try {
        const session = await fetchAuthSession();

        if (session.tokens?.accessToken) {
          sessionStorage.removeItem('newPasswordRequired');
          navigate(AUTH_PATHS.HOME);
        }
      } catch {
        toast.error('Failed to set new password.');
      }
    };

    validateAuthState();
  }, [navigate]);

  const onSubmit = async (data: NewPasswordFormData) => {
    try {
      await mutateAsync({ password: data.password });
      sessionStorage.removeItem('newPasswordRequired');
      toast.success('Password was set successfully.');
      navigate('/');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to set new password.';

      if (
        errorMessage.includes('No active sign-in session') ||
        errorMessage.includes('No pending sign-in') ||
        errorMessage.includes('Invalid session')
      ) {
        sessionStorage.removeItem('newPasswordRequired');
        navigate(AUTH_PATHS.LOGIN);
      }
      toast.error('Failed to set new password.');
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-white">
      <div className="flex w-full flex-1 flex-col items-center justify-center px-4">
        <img
          src={AUTH_ASSETS.FULL_LOGO_BLACK}
          alt="Predoc Logo"
          className="mb-8 h-14"
        />

        <Card className="w-full max-w-[464px] p-8">
          <h1 className="mb-6 text-2xl font-semibold tracking-tight">
            Set New Password
          </h1>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="password-input"
                      className={
                        form.formState.errors.password
                          ? 'border-destructive'
                          : ''
                      }
                      aria-invalid={!!form.formState.errors.password}
                      aria-describedby={
                        form.formState.errors.password
                          ? 'password-error'
                          : undefined
                      }
                      {...form.register('password')}
                    />
                  </FormControl>
                </FormItem>

                <PasswordRequirementsSection password={password} />

                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="confirm-password-input"
                      className={
                        form.formState.errors.confirmPassword
                          ? 'border-destructive'
                          : ''
                      }
                      aria-invalid={!!form.formState.errors.confirmPassword}
                      aria-describedby={
                        form.formState.errors.confirmPassword
                          ? 'confirm-password-error'
                          : undefined
                      }
                      {...form.register('confirmPassword')}
                      showPasswordLabel="Show confirm password"
                      hidePasswordLabel="Hide confirm password"
                    />
                  </FormControl>
                  {form.formState.errors.confirmPassword && (
                    <FormMessage
                      id="confirm-password-error"
                      className="text-destructive mt-1 text-sm"
                      role="alert"
                    >
                      {form.formState.errors.confirmPassword.message}
                    </FormMessage>
                  )}
                </FormItem>
              </div>

              {error && (
                <div
                  className="text-destructive text-center text-sm"
                  role="alert"
                >
                  {error.message}
                </div>
              )}

              <Button
                type="submit"
                disabled={isPending || !form.formState.isValid}
                className="h-11 w-full"
                loading={isPending}
              >
                {isPending ? 'Setting Password...' : 'Set Password'}
              </Button>
            </form>
          </Form>
        </Card>
      </div>
      <Footer />
    </div>
  );
};
